import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, EmptyState } from "@/components/common";
import { api, formatApiErrorDetail, BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCcw, Radio, Play, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

function RotatingPreview({ creatives, interval, testid }) {
  // Server-side weighted rotation happens per-request now. In the panel we just
  // show all serving creatives as a mini grid — no auto-cycling — so the admin
  // can see the full pool that is being weighted at once.
  if (!creatives || creatives.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded flex items-center justify-center min-h-[140px] overflow-hidden">
        <div className="text-xs text-slate-500 py-6 px-4 text-center">
          Bu boyut için aktif kreatif yok. Kreatifler ekranından yükleyin.
        </div>
      </div>
    );
  }
  if (creatives.length === 1) {
    const c = creatives[0];
    return (
      <div className="bg-slate-50 border border-slate-200 rounded flex items-center justify-center min-h-[140px] overflow-hidden">
        <img src={`${BASE}${c.webp_url}`} alt={c.alt_text || ""} className="max-w-full max-h-[220px] object-contain" data-testid={testid} />
      </div>
    );
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2" data-testid={testid}>
      <div className="grid grid-cols-2 gap-2">
        {creatives.slice(0, 4).map((c) => (
          <div key={c.id} className="bg-white rounded border border-slate-100 flex items-center justify-center overflow-hidden aspect-video">
            <img src={`${BASE}${c.webp_url}`} alt={c.alt_text || ""} className="max-w-full max-h-full object-contain" />
          </div>
        ))}
      </div>
      <div className="mt-1.5 text-[10px] text-slate-500 text-center">
        Ağırlıklı rotasyon · her sayfa yüklemesinde biri seçilir
      </div>
    </div>
  );
}

export default function LivePreviewPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [muns, setMuns] = useState([]);
  const [munId, setMunId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapItem, setSwapItem] = useState(null); // { placement, ad_size, current_creative, candidates }
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [swapping, setSwapping] = useState(false);

  async function loadMuns() {
    try {
      const { data: m } = await api.get("/admin/municipalities");
      setMuns(m);
      if (!isSuper && user?.municipality_id) {
        setMunId(user.municipality_id);
      } else if (isSuper && m.length > 0 && !munId) {
        setMunId(m[0].id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function loadPreview(id) {
    if (!id) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/live-preview?municipality_id=${id}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMuns(); }, []);
  useEffect(() => { if (munId) loadPreview(munId); }, [munId]);

  function openSwap(item) {
    setSwapItem(item);
    setSelectedCandidateId(item.current_creative?.id || item.candidates?.[0]?.id || "");
  }

  async function doSwap() {
    if (!selectedCandidateId) { toast.error("Bir kreatif seçin"); return; }
    setSwapping(true);
    try {
      await api.post("/admin/live-preview/swap", { creative_id: selectedCandidateId });
      toast.success("Kreatif yayına alındı — tüm sitelerde bir sonraki gösterimde değişecek.");
      setSwapItem(null);
      await loadPreview(munId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSwapping(false);
    }
  }

  const currentMun = useMemo(() => muns.find((m) => m.id === munId), [muns, munId]);

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Yayın Durumu"
        title="Canlı Önizleme"
        description="Seçtiğiniz belediye için her reklam alanında şu an yayında olan görseli görün ve tek tıkla değiştirin. Kod aynı kalır, tüm yayıncı sitelerde bir sonraki gösterimde yeni görsel çıkar."
        actions={
          <Button
            variant="outline"
            className="rounded-sm h-10"
            onClick={() => loadPreview(munId)}
            data-testid="live-preview-refresh"
          >
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Yenile
          </Button>
        }
      />

      {isSuper && (
        <div className="flex items-center gap-3">
          <Label className="text-xs text-slate-600">Belediye</Label>
          <Select value={munId} onValueChange={setMunId}>
            <SelectTrigger className="w-64 h-9" data-testid="live-preview-mun-select"><SelectValue placeholder="Belediye seçin" /></SelectTrigger>
            <SelectContent>
              {muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {currentMun && <div className="text-xs text-slate-500">{currentMun.city} / {currentMun.district}</div>}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Yükleniyor…</div>
      ) : !data || !data.items?.length ? (
        <EmptyState title="Bu belediye için tanımlı reklam alanı yok" subtitle="Önce Reklam Alanları menüsünden yerleşim oluşturun." testid="live-preview-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="live-preview-grid">
          {data.items.map((it) => {
            const size = it.ad_size || {};
            const rotationList = it.serving_creatives || (it.current_creative ? [it.current_creative] : []);
            const totalCandidates = it.candidates?.length || 0;
            return (
              <div key={it.placement.id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`live-card-${it.placement.id}`}>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{size.name}</div>
                    <div className="font-heading font-semibold text-slate-950 text-sm mt-0.5">{size.width}×{size.height} px</div>
                  </div>
                  {rotationList.length > 1 ? (
                    <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border bg-blue-50 text-blue-700 border-blue-200">
                      <Radio className="w-3 h-3" /> Ağırlıklı Rotasyon ({rotationList.length})
                    </span>
                  ) : rotationList.length === 1 ? (
                    <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Radio className="w-3 h-3" /> Yayında
                    </span>
                  ) : (
                    <span className="inline-flex text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-200">
                      Kreatif Yok
                    </span>
                  )}
                </div>

                <div className="p-4">
                  <RotatingPreview
                    creatives={rotationList}
                    interval={it.rotate_interval_ms || 3000}
                    testid={`live-img-${it.placement.id}`}
                  />

                  <div className="mt-3 space-y-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {rotationList[0]?.alt_text || rotationList[0]?.campaign_name || "—"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      Kampanya: <span className="font-medium text-slate-700">{rotationList[0]?.campaign_name || "—"}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Reklam Alanı: <span className="font-mono">{it.placement.name}</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-500">
                      {totalCandidates} aday kreatif · {rotationList.length} aktif
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8"
                      disabled={totalCandidates === 0}
                      onClick={() => openSwap(it)}
                      data-testid={`swap-btn-${it.placement.id}`}
                    >
                      Değiştir
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Swap dialog */}
      <Dialog open={!!swapItem} onOpenChange={(o) => !o && setSwapItem(null)}>
        <DialogContent className="max-w-2xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {swapItem?.ad_size?.name} · Yayınlanacak Görseli Seçin
            </DialogTitle>
            <DialogDescription>
              Seçilen görsel aktif olur, aynı boyuttaki diğer kreatifler otomatik duraklatılır. Yayıncı sitelerin bir sonraki gösteriminde yeni görsel görünür.
            </DialogDescription>
          </DialogHeader>
          {swapItem && (
            <>
              <div className="text-xs text-slate-500 mb-2">
                Seçtiğiniz görsel aktif olur, aynı boyuttaki diğer görseller otomatik olarak duraklatılır. Yayıncı sitelerin bir sonraki gösteriminde yeni görsel görünür.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1" data-testid="swap-candidates-grid">
                {(swapItem.candidates || []).map((c) => {
                  const selected = selectedCandidateId === c.id;
                  const isCurrent = swapItem.current_creative?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCandidateId(c.id)}
                      className={`text-left rounded border transition-all ${selected ? "border-red-600 ring-2 ring-red-600/20" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`candidate-${c.id}`}
                    >
                      <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden">
                        <img src={`${BASE}${c.webp_url}`} alt={c.alt_text || ""} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <div className="text-xs font-medium text-slate-900 truncate">
                          {c.alt_text || c.campaign_name || "—"}
                        </div>
                        <div className="text-[10.5px] text-slate-500 truncate mt-0.5">{c.campaign_name}</div>
                        <div className="mt-1 flex items-center gap-1">
                          {isCurrent && (
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-0.5">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Şu an yayında
                            </span>
                          )}
                          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${c.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            {c.status}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSwapItem(null)}>İptal</Button>
            <Button
              disabled={swapping || !selectedCandidateId || swapItem?.current_creative?.id === selectedCandidateId}
              onClick={doSwap}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
              data-testid="swap-publish-btn"
            >
              <Play className="w-4 h-4 mr-1.5" />
              {swapping ? "Yayınlanıyor…" : "Yayınla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
